import type { ArcadeQ } from "./types";

/* Short Bahasa Melayu and English word questions for the runner games, generated so there is always a full round:
   spelling (hear the word, run under the right spelling), opposites, BM penjodoh bilangan and English plurals.
   Everything fits on an answer board. This is learning content, so it stays in its own language whatever the UI language is. */

const pick = <T,>(a: readonly T[]) => a[Math.floor(Math.random() * a.length)]!;
const shuffle = <T,>(a: T[]) => { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j]!, a[i]!]; } return a; };
const band = (year: number) => (year <= 2 ? 0 : year <= 4 ? 1 : 2);

const BM_WORDS = [
  ["buku", "kucing", "rumah", "sekolah", "bola", "epal", "ikan", "meja", "kerusi", "pensel", "kasut", "baju", "ayam", "burung", "pokok", "bunga", "kereta", "basikal", "payung", "cawan", "pinggan", "sudu", "tingkap", "pintu", "bulan", "bintang", "gajah", "harimau", "arnab", "katak"],
  ["jambatan", "kampung", "nelayan", "petani", "keluarga", "sayuran", "pelangi", "gunung", "sungai", "hutan", "pantai", "pesawat", "kilang", "bandar", "kantin", "padang", "taman", "jiran", "masjid", "hospital", "bomba", "pasaraya", "durian", "rambutan", "kelapa", "tembikai", "kerbau", "monyet", "lebah", "penyapu"],
  ["perpustakaan", "kebersihan", "kemerdekaan", "tanggungjawab", "persekitaran", "pengetahuan", "kebudayaan", "perpaduan", "kesihatan", "pelancongan", "pembangunan", "teknologi", "komputer", "maklumat", "pertandingan", "persahabatan", "keselamatan", "kecemerlangan", "pemandangan", "perayaan"],
] as const;

const EN_WORDS = [
  ["apple", "chair", "table", "house", "school", "water", "happy", "green", "yellow", "orange", "mother", "father", "sister", "brother", "teacher", "window", "pencil", "rabbit", "monkey", "garden", "kitchen", "friend", "flower", "purple", "banana"],
  ["because", "beautiful", "library", "birthday", "holiday", "weather", "morning", "picture", "children", "vegetable", "elephant", "umbrella", "together", "different", "favourite", "breakfast", "adventure", "tomorrow", "careful", "hospital"],
  ["environment", "necessary", "especially", "knowledge", "celebration", "responsible", "dangerous", "community", "experience", "important", "information", "temperature", "neighbour", "independent", "communication", "government", "description", "beginning", "Wednesday", "February"],
] as const;

const BM_OPP: [string, string][] = [
  ["besar", "kecil"], ["tinggi", "rendah"], ["panjang", "pendek"], ["panas", "sejuk"], ["siang", "malam"], ["cepat", "lambat"], ["gembira", "sedih"],
  ["berat", "ringan"], ["terang", "gelap"], ["buka", "tutup"], ["naik", "turun"], ["masuk", "keluar"], ["kaya", "miskin"], ["rajin", "malas"],
  ["bersih", "kotor"], ["tebal", "nipis"], ["jauh", "dekat"], ["kuat", "lemah"], ["mudah", "susah"], ["tua", "muda"], ["lebar", "sempit"],
  ["penuh", "kosong"], ["awal", "lewat"], ["atas", "bawah"], ["basah", "kering"], ["tajam", "tumpul"], ["kasar", "halus"], ["menang", "kalah"],
];
const EN_OPP: [string, string][] = [
  ["big", "small"], ["hot", "cold"], ["tall", "short"], ["fast", "slow"], ["happy", "sad"], ["up", "down"], ["day", "night"], ["heavy", "light"],
  ["old", "new"], ["empty", "full"], ["clean", "dirty"], ["strong", "weak"], ["early", "late"], ["rich", "poor"], ["easy", "difficult"],
  ["wet", "dry"], ["loud", "quiet"], ["near", "far"], ["thick", "thin"], ["hard", "soft"], ["push", "pull"], ["open", "closed"], ["win", "lose"],
  ["inside", "outside"], ["first", "last"], ["sharp", "blunt"], ["arrive", "leave"], ["remember", "forget"],
];

/* penjodoh bilangan: the classifier and nouns that take it */
const BM_PB: [string, string[]][] = [
  ["Seekor", ["kucing", "ikan", "ayam", "gajah", "burung", "arnab", "semut", "lembu"]],
  ["Sebuah", ["rumah", "kereta", "sekolah", "kapal", "buku", "bandar", "negeri", "almari"]],
  ["Sebatang", ["pensel", "pokok", "sungai", "jalan", "pen", "tiang", "lilin", "kayu"]],
  ["Sehelai", ["baju", "kertas", "daun", "kain", "tuala", "bendera", "seluar", "rambut"]],
  ["Sebiji", ["bola", "epal", "cawan", "telur", "guli", "bantal", "pinggan", "limau"]],
  ["Seorang", ["guru", "murid", "doktor", "petani", "polis", "budak", "jururawat", "nelayan"]],
  ["Sekuntum", ["bunga", "mawar", "orkid"]],
  ["Sekeping", ["gambar", "roti", "papan", "kad", "surat khabar", "duit syiling"]],
  ["Sepucuk", ["surat", "surat jemputan"]],
];

const EN_PLURAL: [string, string][] = [
  ["box", "boxes"], ["baby", "babies"], ["child", "children"], ["mouse", "mice"], ["foot", "feet"], ["tooth", "teeth"], ["man", "men"],
  ["woman", "women"], ["leaf", "leaves"], ["knife", "knives"], ["bus", "buses"], ["city", "cities"], ["sheep", "sheep"], ["goose", "geese"],
  ["person", "people"], ["story", "stories"], ["watch", "watches"], ["toy", "toys"], ["potato", "potatoes"], ["wolf", "wolves"],
  ["dish", "dishes"], ["lady", "ladies"], ["fox", "foxes"], ["key", "keys"], ["half", "halves"], ["tomato", "tomatoes"],
];

const VOWEL_SWAP: Record<string, string[]> = { a: ["e", "o"], e: ["a", "i"], i: ["e", "y"], o: ["u", "a"], u: ["o", "a"] };

/** Three different plausible misspellings of a word. */
export function misspell(word: string, n = 3): string[] {
  const out = new Set<string>();
  const w = word.toLowerCase(), cap = word[0] !== word[0]!.toLowerCase();
  const fix = (x: string) => (cap ? x[0]!.toUpperCase() + x.slice(1) : x);
  const tries: (() => string)[] = [
    () => { const i = 1 + Math.floor(Math.random() * (w.length - 2)); return w.slice(0, i) + w[i + 1] + w[i] + w.slice(i + 2); },   // swap two letters
    () => { const i = 1 + Math.floor(Math.random() * (w.length - 1)); return w.slice(0, i) + w.slice(i + 1); },                     // drop one
    () => { const i = 1 + Math.floor(Math.random() * (w.length - 1)); return /[aeiou\-]/.test(w[i]!) ? w : w.slice(0, i) + w[i] + w.slice(i); }, // double a consonant
    () => { const idx = [...w].map((c, i) => (VOWEL_SWAP[c] && i > 0 ? i : -1)).filter((i) => i > 0); if (!idx.length) return w; const i = pick(idx); return w.slice(0, i) + pick(VOWEL_SWAP[w[i]!]!) + w.slice(i + 1); },
  ];
  for (let k = 0; k < 60 && out.size < n; k++) {
    const x = pick(tries)();
    if (x !== w && x.length >= 2 && !x.includes("--")) out.add(fix(x));
  }
  return [...out];
}

function spelling(word: string, lang: "ms" | "en"): ArcadeQ | null {
  const wrong = misspell(word);
  if (wrong.length < 3) return null;
  const options = shuffle([word, ...wrong]);
  return { text: lang === "ms" ? "Dengar. Ejaan yang betul?" : "Listen. Which spelling is correct?", options, answer: options.indexOf(word), lang, say: word };
}

function opposite(pairs: [string, string][], lang: "ms" | "en"): ArcadeQ {
  const [a, b] = pick(pairs);
  const flip = Math.random() < 0.5, word = flip ? b : a, ans = flip ? a : b;
  const others = shuffle(pairs.filter(([x, y]) => x !== a && y !== b).flat()).slice(0, 3);
  const options = shuffle([ans, ...others]);
  return { text: lang === "ms" ? `Lawan kata "${word}"?` : `Opposite of "${word}"?`, options, answer: options.indexOf(ans), lang, say: lang === "ms" ? `Lawan kata ${word}` : `Opposite of ${word}` };
}

function classifier(): ArcadeQ {
  const [pb, nouns] = pick(BM_PB);
  const noun = pick(nouns);
  // never offer a second classifier that also fits this noun
  const others = shuffle(BM_PB.filter(([x, ns]) => x !== pb && !ns.includes(noun)).map(([x]) => x)).slice(0, 3);
  const options = shuffle([pb, ...others]);
  return { text: `___ ${noun}`, options, answer: options.indexOf(pb), lang: "ms", say: `Penjodoh bilangan untuk ${noun}` };
}

function plural(): ArcadeQ {
  const [one, many] = pick(EN_PLURAL);
  const cands = [one + "s", one + "es", one.replace(/y$/, "ies"), one.replace(/fe?$/, "ves"), one + "ren", one.replace(/oo/, "ee"), ...misspell(many)];
  const wrong = shuffle([...new Set(cands.filter((x) => x !== many && x !== one))]).slice(0, 3);
  const options = shuffle([many, ...wrong]);
  return { text: `One ${one}, two ___?`, options, answer: options.indexOf(many), lang: "en", say: `One ${one}, two...` };
}

/** `n` generated questions for Bahasa Melayu ("bm") or English ("bi") at the given school year (1–6). */
export function bahasaRound(subject: "bm" | "bi", year: number, n: number): ArcadeQ[] {
  const b = band(year), out: ArcadeQ[] = [], seen = new Set<string>();
  const words = subject === "bm" ? BM_WORDS : EN_WORDS;
  for (let k = 0; k < n * 8 && out.length < n; k++) {
    const r = Math.random();
    let q: ArcadeQ | null;
    if (subject === "bm") q = r < 0.45 ? spelling(pick(words[b]!), "ms") : r < 0.75 ? opposite(BM_OPP, "ms") : classifier();
    else q = r < 0.45 ? spelling(pick(words[b]!), "en") : r < 0.75 || b === 0 ? opposite(EN_OPP, "en") : plural();
    if (!q || q.answer < 0 || new Set(q.options).size !== 4) continue;
    const key = q.text + q.options[q.answer];
    if (seen.has(key)) continue;
    seen.add(key); out.push(q);
  }
  return out;
}
